/* eslint-disable @typescript-eslint/naming-convention -- Many of these fields are named on the model, and changing them here is out of scope. */
import { useEffect, type FunctionComponent, type ReactNode, type VoidFunctionComponent, useCallback } from 'react';
import { defaultScheduledUpdateHook, hookTypeToLabelMap } from './useProjectUpdateHooksSettings';
import CopyPreformattedToClipboard from '../../../generic/CopyPreformattedToClipboard';
import { FormLabelVariant, LabeledTextInput } from '../../../generic/Forms';
import Button, { NavigationButton } from '../../../generic/Button';
import Link from '../../../generic/Link';
import Tooltip from '../../../generic/Tooltip';
import { generateSecret, nuc } from '../../../../utils';
import Section from '../../../shared/settings/SettingsPageSection';
import Paragraph from '../../../shared/settings/SettingsPageParagraph';
import ProjectUpdateHookSettings from '../../../shared/settings/ProjectUpdateHookSettings';
import type { ProjectUpdateHooksSettings, ProjectUpdateHook } from './types';
import type { UpdateHookType } from '../../../../../shared/model-typings';
import styles from './ProjectUpdateHooks.module.less';
import type { Project } from '../../../../types';

//  Simple hook which manages the generation of a random secret key.
const useGenerateRandomSecret = ({ secretKey, onSecretKeyChange }: {
  secretKey: string;
  onSecretKeyChange: (key: string) => void;
}) => {
  const regenerateKey = useCallback(() => onSecretKeyChange(generateSecret()), [onSecretKeyChange]);

  //  If there is no secretKey set, we generate one on mount.
  useEffect(() => {
    if (!secretKey) {
      regenerateKey();
    }
  }, [regenerateKey, secretKey]);
  return regenerateKey;
};

const getWebhookURL = (hookType: UpdateHookType, locator: string): string => {
  if (hookType === 'circleci' || hookType === 'travisci') {
    return `${window.location.origin}/hooks/${hookType}`;
  }
  return `${window.location.origin}/hooks/${hookType}/${nuc(locator)}`;
};

const getEnsureWebhookURL = (
  hookType: UpdateHookType,
  locator: string,
  vcsHost?: Project['vcs_host'],
): `${string}/services/${UpdateHookType | 'github-app'}/ensureWebhook?locator=${string}` => {
  const resolvedHookType = vcsHost === 'github-app' ? 'github-app' : hookType;

  return `${window.location.origin}/services/${resolvedHookType}/ensureWebhook?locator=${nuc(locator)}`;
};

/**
 * Displayed when hookType is 'none'
 */
const None: VoidFunctionComponent = () => (
  <>
    <div className="margin-bottom-md">
      <Section.Heading>
        Manual Updates
      </Section.Heading>
    </div>
    <Paragraph size="medium">
      This project is not set up to receive automatic updates.
    </Paragraph>
  </>
);

type GenericUpdateHookConfigProps = {
  hookType: UpdateHookType; // Note that this is not nullable.
  additionalInfo: ReactNode;
  locator: string;
  includeRegenerateHookLink?: boolean;
  vcsHost?: Project['vcs_host'];
};

/**
 * Displayed when hookType is 'webhook', and extended for other types.
 */
const Generic: FunctionComponent<GenericUpdateHookConfigProps> = ({
  hookType,
  additionalInfo,
  locator,
  includeRegenerateHookLink,
  children,
  vcsHost,
}) => (
  <>
    <div className="margin-bottom-md">
      <Section.Heading>
        {hookTypeToLabelMap[hookType]} Update URL
      </Section.Heading>
    </div>
    {additionalInfo && (
      <Paragraph size="medium">
        {additionalInfo}
      </Paragraph>
    )}
    <CopyPreformattedToClipboard text={getWebhookURL(hookType, locator)} />
    <p className={styles.messageParagraph}>
      Pinging this URL will trigger an update on on this project.
    </p>
    {children}
    {includeRegenerateHookLink && (
      <Tooltip
        id="regenerate-hook-tooltip"
        tooltip={`Requires ${hookTypeToLabelMap[hookType]} login.`}
        placement="right"
        showTooltip
      >
        <div className="inline-block">
          <NavigationButton
            to={getEnsureWebhookURL(hookType, locator, vcsHost)}
            size="large"
            type="secondary"
            openInNewTab
          >
            Auto-regenerate Hook
          </NavigationButton>
        </div>
      </Tooltip>
    )}
  </>
);

type GenericWithSecretProps = GenericUpdateHookConfigProps & {
  secretKey: string;
  onSecretKeyChange: (secretKey: string) => void;
};

/**
 * Extension of the Generic above, but it contains a `secretKey` field.
 */
const GenericWithSecret: FunctionComponent<GenericWithSecretProps> = ({ secretKey, onSecretKeyChange, ...props }) => {
  const regenerateKey = useGenerateRandomSecret({ secretKey, onSecretKeyChange });
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Generic {...props} includeRegenerateHookLink>
      <LabeledTextInput
        label="Secret Key"
        value={secretKey}
        labelVariant={FormLabelVariant.Filter}
        onChange={onSecretKeyChange}
        isDisabled
        isCopyable
        suffix={(
          <Button
            size="micro"
            type="secondary"
            onClick={regenerateKey}
          >
            Regenerate
          </Button>
        )}
      />
      <Paragraph size="medium">
        Secret key for {hookTypeToLabelMap[props.hookType]} webhook.
        {' '}
        The {hookTypeToLabelMap[props.hookType]} webhook should use this secret key to sign requests.
      </Paragraph>
    </Generic>
  );
};

//  Config component for the webhook hookType.
const ProjectUpdateHooksSettingsWebhookConfig: FunctionComponent<Pick<GenericUpdateHookConfigProps, 'locator' | 'hookType'>> = ({ locator, hookType }) => (
  <Generic
    locator={locator}
    hookType={hookType}
    additionalInfo="Use the information below to create your webhook:"
  >
    <Paragraph size="medium">
      Note: Bitbucket Server/Stash users must enable
      {' '}
      <Link to="https://confluence.atlassian.com/bitbucketserver076/post-service-webhook-for-bitbucket-server-1026535477.html" openInNewTab>
        post-receive Webhooks
      </Link>
      {' '}
      on their repos.
    </Paragraph>
  </Generic>
);

//  Config component for the github hookType.
const ProjectUpdateHooksSettingsGitHubConfig: FunctionComponent<
  Pick<GenericWithSecretProps, 'locator' | 'hookType' | 'secretKey' | 'onSecretKeyChange'> & {
  vcsHost: Project['vcs_host'] | null; // Determine if the VCS host is a GitHub App or not.
}
> = ({ locator, hookType, secretKey, onSecretKeyChange, vcsHost }) => (
  <GenericWithSecret
    locator={locator}
    hookType={hookType}
    additionalInfo={
      <>
        Go to your GitHub repository&apos;s <b>Settings</b> page, click on <b>Integrations</b> and then{' '}
        <b>Add webhook</b>. Then, use the URL below to set up your webhook:
      </>
    }
    secretKey={secretKey}
    onSecretKeyChange={onSecretKeyChange}
    vcsHost={vcsHost || undefined}
  />
);

//  Config component for the gitlab hookType.
const ProjectUpdateHooksSettingsGitLabConfig: FunctionComponent<Pick<GenericWithSecretProps, 'locator' | 'hookType' | 'secretKey' | 'onSecretKeyChange'>> = ({
  locator,
  hookType,
  secretKey,
  onSecretKeyChange,
}) => (
  <GenericWithSecret
    locator={locator}
    hookType={hookType}
    additionalInfo={(
      <>
        Go to your GitLab repository&apos;s <b>Settings</b> page, click on <b>Integrations</b>
        {' '}
        and then <b>Add webhook</b>. Then, use the URL below to set up your webhook:
      </>
    )}
    secretKey={secretKey}
    onSecretKeyChange={onSecretKeyChange}
  />
);

//  Config component for the bitbucket_cloud hookType.
const ProjectUpdateHooksSettingsBitbucketCloudConfig: FunctionComponent<Pick<GenericUpdateHookConfigProps, 'locator' | 'hookType'>> = ({ locator, hookType }) => (
  <Generic
    locator={locator}
    hookType={hookType}
    additionalInfo={(
      <>
        Go to your BitBucket repository&apos;s <b>Settings</b> page, click on <b>Integrations</b>
        {' '}
        and then <b>Add webhook</b>. Then, use the URL below to set up your webhook:
      </>
    )}
    includeRegenerateHookLink
  />
);

//  Config component for the azure_repos hookType.
const ProjectUpdateHooksSettingsAzureReposConfig: FunctionComponent<Pick<GenericWithSecretProps, 'locator' | 'hookType' | 'secretKey' | 'onSecretKeyChange'>> = ({
  locator,
  hookType,
  secretKey,
  onSecretKeyChange,
}) => (
  <GenericWithSecret
    locator={locator}
    hookType={hookType}
    additionalInfo={(
      <>
        Go to your Azure Repos project&apos;s <b>Settings</b> page, click on <b>Service hooks</b>.
        {' '}
        Then, use the URL below to set up your webhook:
      </>
    )}
    secretKey={secretKey}
    onSecretKeyChange={onSecretKeyChange}
  />
);

//  Config component for the circleci hookType.
const ProjectUpdateHooksSettingsCircleCIConfig: FunctionComponent<Pick<GenericUpdateHookConfigProps, 'locator' | 'hookType'>> = ({ locator, hookType }) => (
  <Generic
    locator={locator}
    hookType={hookType}
    additionalInfo={(
      <>
        Go to the <Link to="https://circleci.com/docs/1.0/configuration/#notify" openInNewTab>CircleCI Docs</Link> for info on setting hooks. Use the URL below to set up your webhook:
      </>
    )}
  >
    <Paragraph size="medium">
      Note: FOSSA will only trigger a build on a successful CircleCI build
    </Paragraph>
  </Generic>
);

//  Config component for the travisci hookType.
const ProjectUpdateHooksSettingsTravisCIConfig: FunctionComponent<Pick<GenericUpdateHookConfigProps, 'locator' | 'hookType'>> = ({ locator, hookType }) => (
  <Generic
    locator={locator}
    hookType={hookType}
    additionalInfo={(
      <>
        Go to the <Link to="https://docs.travis-ci.com/user/notifications#Configuring-webhook-notifications" openInNewTab>TravisCI Docs</Link> for info on setting hooks. Use the URL below to set up your webhook:
      </>
    )}
  >
    <Paragraph size="medium">
      Note: FOSSA will only trigger a build on a successful TravisCI build
    </Paragraph>
  </Generic>
);

type Props = {
  locator: string;
  isSaving: boolean;
  hookType: UpdateHookType | null;
  updateHook: ProjectUpdateHook | null;
  onUpdate: (updateHook: Partial<ProjectUpdateHooksSettings>) => void;
  vcsHost: Project['vcs_host'] | null;
};

/**
 * ProjectUpdateHooksSettingsHookConfig
 */
const ProjectUpdateHooksSettingsHookConfigBody: VoidFunctionComponent<Props> = ({
  locator,
  hookType,
  isSaving,
  updateHook,
  onUpdate,
  vcsHost,
}) => {
  switch (hookType) {
    case 'scheduled':
      return (
        <ProjectUpdateHookSettings
          isLoading={isSaving}
          isUpdateHooksEnabled={updateHook?.active ?? false}
          onToggleIsUpdateHooksEnabled={(active) =>
            onUpdate({ updateHook: { ...updateHook, active } as ProjectUpdateHook })}
          scheduledInterval={updateHook?.scheduled_interval ?? defaultScheduledUpdateHook.scheduled_interval}
          onScheduledIntervalChange={(scheduled_interval) =>
            onUpdate({ updateHook: { ...updateHook, scheduled_interval } as ProjectUpdateHook })}
          scheduledIntervalLength={updateHook?.scheduled_interval_length ??
            defaultScheduledUpdateHook.scheduled_interval_length}
          onScheduledIntervalLengthChange={(scheduled_interval_length) =>
            onUpdate({ updateHook: { ...updateHook, scheduled_interval_length } as ProjectUpdateHook })}
          scheduledIntervalTime={updateHook?.scheduled_interval_time ??
            defaultScheduledUpdateHook.scheduled_interval_time}
          onScheduledIntervalTimeChange={(scheduled_interval_time) =>
            onUpdate({ updateHook: { ...updateHook, scheduled_interval_time } as ProjectUpdateHook })}
        />
      );
    case 'webhook':
      return <ProjectUpdateHooksSettingsWebhookConfig locator={locator} hookType={hookType} />;
    case 'github':
      return (
        <ProjectUpdateHooksSettingsGitHubConfig
          locator={locator}
          hookType={hookType}
          secretKey={updateHook?.secret_key ?? ''}
          onSecretKeyChange={(secret_key) =>
            onUpdate({ updateHook: { ...updateHook, secret_key } as ProjectUpdateHook })}
          vcsHost={vcsHost}
        />
      );
    case 'gitlab':
      return (
        <ProjectUpdateHooksSettingsGitLabConfig
          locator={locator}
          hookType={hookType}
          secretKey={updateHook?.secret_key ?? ''}
          onSecretKeyChange={(secret_key) =>
            onUpdate({ updateHook: { ...updateHook, secret_key } as ProjectUpdateHook })}
        />
      );
    case 'bitbucket_cloud':
      return <ProjectUpdateHooksSettingsBitbucketCloudConfig locator={locator} hookType={hookType} />;
    case 'azure_repos':
      return (
        <ProjectUpdateHooksSettingsAzureReposConfig
          locator={locator}
          hookType={hookType}
          secretKey={updateHook?.secret_key ?? ''}
          onSecretKeyChange={(secret_key) =>
            onUpdate({ updateHook: { ...updateHook, secret_key } as ProjectUpdateHook })}
        />
      );
    case 'circleci':
      return <ProjectUpdateHooksSettingsCircleCIConfig locator={locator} hookType={hookType} />;
    case 'travisci':
      return <ProjectUpdateHooksSettingsTravisCIConfig locator={locator} hookType={hookType} />;
    default:
      return <None />;
  }
};

const ProjectUpdateHooksSettingsHookConfig: VoidFunctionComponent<Props> = (props) => (
  <div className={styles.message}>
    <ProjectUpdateHooksSettingsHookConfigBody
      locator={props.locator}
      hookType={props.hookType}
      isSaving={props.isSaving}
      updateHook={props.updateHook}
      onUpdate={props.onUpdate}
      vcsHost={props.vcsHost}
    />
  </div>
);

export default ProjectUpdateHooksSettingsHookConfig;
